<template>
  <section class="content-panel">
    <div class="section-head">
      <div>
        <h2>阿里云风控配置</h2>
        <p>维护多家公司的 AccessKey，供应用绑定使用</p>
      </div>
      <div class="head-actions">
        <el-input
          v-model.trim="keyword"
          placeholder="搜索配置名称"
          clearable
          class="search-input"
          :prefix-icon="Search"
          @keyup.enter="search"
          @clear="search"
        />
        <el-button type="primary" @click="search">查询</el-button>
        <el-button type="primary" @click="openCreate">
          <el-icon><Plus /></el-icon>新增配置
        </el-button>
      </div>
    </div>

    <el-table :data="rows" stripe v-loading="loading">
      <el-table-column type="index" label="#" width="56" />
      <el-table-column prop="name" label="配置名称" min-width="140" />
      <el-table-column prop="regionId" label="地域" width="120" />
      <el-table-column prop="accessKeyId" label="AccessKey ID" min-width="160" show-overflow-tooltip />
      <el-table-column label="降级放行" width="100" align="center">
        <template #default="{ row }">
          <el-tag :type="row.failOpen === 1 ? 'success' : 'warning'" effect="light">
            {{ row.failOpen === 1 ? '是' : '否' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="忽略标签" min-width="220" show-overflow-tooltip>
        <template #default="{ row }">{{ formatIgnoreTags(row.ignoreTags) }}</template>
      </el-table-column>
      <el-table-column label="状态" width="90">
        <template #default="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'info'" effect="light">
            {{ row.status === 1 ? '启用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openEdit(row)">编辑</el-button>
          <el-button link :type="row.status === 1 ? 'danger' : 'success'" @click="toggleStatus(row)">
            {{ row.status === 1 ? '停用' : '启用' }}
          </el-button>
        </template>
      </el-table-column>
      <template #empty><el-empty description="暂无风控配置，请先新增" /></template>
    </el-table>

    <div class="panel-footer">
      <el-pagination
        :current-page="pagination.page"
        :page-size="pagination.size"
        :page-sizes="[10, 20, 50]"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next"
        background
        @current-change="onPageChange"
        @size-change="onSizeChange"
      />
    </div>

    <el-dialog v-model="dialog" :title="editingId ? '编辑风控配置' : '新增风控配置'" width="720px">
      <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
        <el-form-item label="配置名称" prop="name">
          <el-input v-model.trim="form.name" placeholder="如：公司A / 主账号" />
        </el-form-item>
        <el-form-item label="地域" prop="regionId">
          <el-input v-model.trim="form.regionId" placeholder="cn-shanghai" />
        </el-form-item>
        <template v-if="editingId">
          <el-form-item label="当前 AccessKey ID">
            <el-input :model-value="maskedAccessKeyId" disabled />
          </el-form-item>
          <el-form-item label="更换 AccessKey ID（可选）">
            <el-input v-model.trim="form.accessKeyId" placeholder="不更换请留空" />
          </el-form-item>
          <el-form-item label="更换 AccessKey Secret（可选）">
            <el-input v-model="form.accessKeySecret" show-password placeholder="不更换请留空" />
          </el-form-item>
        </template>
        <template v-else>
          <el-form-item label="AccessKey ID" prop="accessKeyId">
            <el-input v-model.trim="form.accessKeyId" placeholder="LTAI..." />
          </el-form-item>
          <el-form-item label="AccessKey Secret" prop="accessKeySecret">
            <el-input v-model="form.accessKeySecret" show-password placeholder="请输入密钥" />
          </el-form-item>
        </template>
        <div class="form-row">
          <el-form-item label="异常降级放行">
            <el-switch v-model="form.failOpen" :active-value="1" :inactive-value="0" />
          </el-form-item>
          <el-form-item label="状态">
            <el-switch v-model="form.status" :active-value="1" :inactive-value="0" active-text="启用" inactive-text="停用" />
          </el-form-item>
        </div>
        <el-form-item label="忽略标签">
          <el-select
            v-model="form.ignoreTagList"
            multiple
            filterable
            collapse-tags
            collapse-tags-tooltip
            :max-collapse-tags="3"
            placeholder="从风险列表中选择要忽略的标签"
            style="width: 100%"
          >
            <el-option-group v-for="group in tagGroups" :key="group.type" :label="group.type">
              <el-option
                v-for="item in group.items"
                :key="item.tagState"
                :label="`${item.tagMean}（${item.tagState}）`"
                :value="item.tagState"
              />
            </el-option-group>
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model.trim="form.remark" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialog = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submit">{{ editingId ? '保存' : '创建' }}</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { Plus, Search } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'

const props = defineProps({ admin: Object })

const loading = ref(false)
const saving = ref(false)
const dialog = ref(false)
const editingId = ref(null)
const maskedAccessKeyId = ref('')
const formRef = ref()
const keyword = ref('')
const rows = ref([])
const tagOptions = ref([])
const pagination = reactive({ page: 1, size: 20, total: 0 })

const empty = () => ({
  name: '',
  regionId: 'cn-shanghai',
  accessKeyId: '',
  accessKeySecret: '',
  serviceCode: 'device_risk',
  failOpen: 1,
  ignoreTagList: ['is_newDevice', 'is_noSim'],
  status: 1,
  remark: ''
})
const form = reactive(empty())

const tagMap = computed(() => {
  const map = {}
  for (const item of tagOptions.value) {
    if (item?.tagState) map[item.tagState] = item
  }
  return map
})

const tagGroups = computed(() => {
  const groups = []
  const index = {}
  for (const item of tagOptions.value) {
    const type = item.tagType || '其他'
    if (!index[type]) {
      index[type] = { type, items: [] }
      groups.push(index[type])
    }
    index[type].items.push(item)
  }
  return groups
})

function splitTags(raw) {
  if (!raw) return []
  if (Array.isArray(raw)) return raw.filter(Boolean)
  return String(raw).split(',').map((s) => s.trim()).filter(Boolean)
}

function formatIgnoreTags(raw) {
  const codes = splitTags(raw)
  if (!codes.length) return '—'
  return codes.map((code) => tagMap.value[code]?.tagMean || code).join('、')
}

const rules = {
  name: [{ required: true, message: '请输入配置名称' }],
  accessKeyId: [{
    validator: (_r, v, cb) => {
      if (!editingId.value && (!v || !String(v).trim())) {
        cb(new Error('请输入 AccessKey ID'))
        return
      }
      cb()
    },
    trigger: 'blur'
  }],
  accessKeySecret: [{
    validator: (_r, v, cb) => {
      if (!editingId.value && (!v || !String(v).trim())) {
        cb(new Error('请输入 AccessKey Secret'))
        return
      }
      cb()
    },
    trigger: 'blur'
  }]
}

async function loadList() {
  loading.value = true
  try {
    const params = new URLSearchParams({
      page: String(pagination.page),
      size: String(pagination.size)
    })
    if (keyword.value) params.set('keyword', keyword.value)
    const data = await props.admin.call(`/api/admin/risk-config/list?${params}`)
    rows.value = data?.records || []
    pagination.total = Number(data?.total || 0)
  } catch (error) {
    ElMessage.error(error.message)
  } finally {
    loading.value = false
  }
}

async function search() {
  pagination.page = 1
  await loadList()
}

async function onPageChange(page) {
  pagination.page = page
  await loadList()
}

async function onSizeChange(size) {
  pagination.size = size
  pagination.page = 1
  await loadList()
}

function defaultIgnoreTags() {
  const defaults = tagOptions.value
    .filter((item) => item.isDefault === 1)
    .map((item) => item.tagState)
    .filter(Boolean)
  return defaults.length ? defaults : ['is_newDevice', 'is_noSim']
}

function openCreate() {
  editingId.value = null
  maskedAccessKeyId.value = ''
  Object.assign(form, empty())
  form.ignoreTagList = defaultIgnoreTags()
  dialog.value = true
}

function openEdit(row) {
  editingId.value = row.id
  maskedAccessKeyId.value = row.accessKeyId || ''
  Object.assign(form, {
    name: row.name,
    regionId: row.regionId || 'cn-shanghai',
    accessKeyId: '',
    accessKeySecret: '',
    serviceCode: row.serviceCode || 'device_risk',
    failOpen: row.failOpen ?? 1,
    ignoreTagList: splitTags(row.ignoreTags),
    status: row.status ?? 1,
    remark: row.remark || ''
  })
  dialog.value = true
}

async function submit() {
  if (!await formRef.value.validate().catch(() => false)) return
  saving.value = true
  try {
    const payload = {
      name: form.name,
      regionId: form.regionId,
      serviceCode: form.serviceCode || 'device_risk',
      failOpen: form.failOpen,
      ignoreTags: (form.ignoreTagList || []).join(','),
      status: form.status,
      remark: form.remark
    }
    if (form.accessKeyId) payload.accessKeyId = form.accessKeyId
    if (form.accessKeySecret) payload.accessKeySecret = form.accessKeySecret

    if (editingId.value) {
      await props.admin.call(`/api/admin/risk-config/${editingId.value}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      })
      ElMessage.success('配置已更新')
    } else {
      payload.accessKeyId = form.accessKeyId
      payload.accessKeySecret = form.accessKeySecret
      await props.admin.call('/api/admin/risk-config/create', {
        method: 'POST',
        body: JSON.stringify(payload)
      })
      ElMessage.success('配置已创建')
    }
    dialog.value = false
    await loadList()
    await props.admin.loadRiskConfigOptions?.()
  } catch (error) {
    ElMessage.error(error.message)
  } finally {
    saving.value = false
  }
}

async function toggleStatus(row) {
  const status = row.status === 1 ? 0 : 1
  try {
    await ElMessageBox.confirm(`确认${status ? '启用' : '停用'}配置「${row.name}」？`, '状态变更', { type: 'warning' })
  } catch {
    return
  }
  try {
    await props.admin.call(`/api/admin/risk-config/${row.id}/status?status=${status}`, { method: 'PUT' })
    ElMessage.success(status ? '已启用' : '已停用')
    await loadList()
    await props.admin.loadRiskConfigOptions?.()
  } catch (error) {
    ElMessage.error(error.message)
  }
}

async function loadTagOptions() {
  try {
    tagOptions.value = (await props.admin.call('/api/admin/risk-tags/options')) || []
  } catch {
    tagOptions.value = []
  }
}

onMounted(async () => {
  await loadTagOptions()
  await loadList()
})
</script>
